import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  PlusIcon,
  EditIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  PlayIcon,
  TrashIcon,
  RefreshCwIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import UpcomingJobsModal from '../components/UpcomingJobsModal'
import { ColumnSelector } from '../components/ColumnSelector'
import { ListFilterSortBar } from '../components/ListFilterSortBar'
import { MobileStatusChips } from '../components/MobileStatusChips'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  mobile,
} from '../components/mobile'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { useDivision } from '../contexts/DivisionContext'
import { recordMatchesDivision } from '../lib/operationalDivisions'
import { usePermissions } from '../hooks/usePermissions'
import { useAuth } from '../contexts/AuthContext'
import { resolveEmployeeIdForUser } from '../lib/timeClockActions'
import {
  fetchJobsByIds,
  resolveAssignedJobsScope,
  canViewAllJobs,
} from '../lib/jobVisibility'
import {
  EMPTY_DATE_RANGE,
  matchesDateRange,
  matchesMultiSelect,
  sortItems,
  uniqueFilterOptions,
  type DateRange,
  type SortDir,
} from '../lib/listFilterSort'
import { getAppLocale } from '../i18n'
import { formatFieldDate } from '../i18n/localeFormat'
import { translateJobStatus, translatePriority } from '../i18n/labels'

interface Job {
  id: string
  job_number: string
  title: string
  description?: string
  service_type?: string
  operational_division_id?: string | null
  scheduled_date?: string
  scheduled_start_time?: string
  scheduled_end_time?: string
  estimated_duration?: number
  status: 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'pending_review'
  priority: 'low' | 'medium' | 'high'
  quoted_amount?: number
  actual_amount?: number
  customer_id: string
  property_id: string
  proposal_id?: string
  contract_id?: string
  billing_type?: string
  is_recurring?: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  notes?: string
  created_at: string
  updated_at: string
  // Joined data
  customer?: {
    company_name?: string
    contact_first_name: string
    contact_last_name: string
  }
  property?: {
    name: string
    address: string
    city?: string
    state?: string
  }
  proposal?: {
    title: string
    proposal_number: string
  }
  contract?: {
    id: string
    title: string
    contract_number: string
    contract_type: 'one_time' | 'recurring' | 'as_needed'
  }
}

export function Jobs() {
  const { t } = useTranslation('jobs')
  const { t: tc } = useTranslation('common')
  const locale = getAppLocale()
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const { user } = useAuth()
  const { can, canSeePricing, isAdmin, loading: permLoading } = usePermissions()
  const viewAllJobs = canViewAllJobs(can, isAdmin)
  const canCreateJob = can('jobs.create')
  const canEditJob = can('jobs.edit')
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [priorityFilter, setPriorityFilter] = useState<string[]>([])
  const [sourceFilter, setSourceFilter] = useState<string[]>([]) // 'direct' | 'proposal' | 'contract'
  const [customerFilter, setCustomerFilter] = useState<string[]>([])
  const [propertyFilter, setPropertyFilter] = useState<string[]>([])
  const [billingFilter, setBillingFilter] = useState<string[]>([])
  const [recurringFilter, setRecurringFilter] = useState('all') // 'all' | 'recurring' | 'single'
  const [scheduledRange, setScheduledRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [createdRange, setCreatedRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [sortBy, setSortBy] = useState('date')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [upcomingJobsModal, setUpcomingJobsModal] = useState<{
    isOpen: boolean
    jobId: string
    jobData: Job | null
  }>({
    isOpen: false,
    jobId: '',
    jobData: null,
  })

  useEffect(() => {
    if (permLoading) return
    fetchJobs()
  }, [currentDivision, user?.id, viewAllJobs, permLoading])

  const fetchJobs = async () => {
    try {
      setLoading(true)

      const jobSelect = `
          *,
          customer:customers(id, company_name, contact_first_name, contact_last_name),
          property:properties(id, name, address, city, state),
          proposal:proposals(id, title, proposal_number),
          contract:contracts!jobs_contract_id_fkey(id, title, contract_number, contract_type)
        `

      let assignedScope: Awaited<ReturnType<typeof resolveAssignedJobsScope>> | null = null
      if (!viewAllJobs) {
        if (!user?.id) {
          setJobs([])
          return
        }
        const empId = await resolveEmployeeIdForUser(user.id)
        if (!empId) {
          setJobs([])
          return
        }
        assignedScope = await resolveAssignedJobsScope(empId)
      }

      // Get jobs with joined customer, property, proposal, and contract data
      let query = supabase.from('jobs').select(jobSelect).order('scheduled_date', { ascending: true })

      if (assignedScope) {
        query = query.or(assignedScope.orFilter)
      }

      const { data: jobsData, error: jobsError } = await query

      if (jobsError) throw jobsError

      const byId = new Map<string, Job>()
      for (const job of jobsData || []) byId.set(job.id, job as Job)

      if (assignedScope?.extraJobIds.length) {
        const extras = await fetchJobsByIds(assignedScope.extraJobIds, jobSelect)
        for (const row of extras) {
          if (typeof row.id === 'string') byId.set(row.id, row as unknown as Job)
        }
      }

      setJobs(
        Array.from(byId.values()).filter((job) => recordMatchesDivision(job, currentDivision))
      )
    } catch (error) {
      console.error('Error fetching jobs:', error)
    } finally {
      setLoading(false)
    }
  }

  const openUpcomingJobsModal = (job: Job) => {
    setUpcomingJobsModal({
      isOpen: true,
      jobId: job.id,
      jobData: job,
    })
  }

  const closeUpcomingJobsModal = () => {
    setUpcomingJobsModal({
      isOpen: false,
      jobId: '',
      jobData: null,
    })
  }

  const handleViewJob = (jobId: string) => {
    navigate(`/jobs/details/${jobId}`)
  }

  const editJob = (jobId: string) => {
    navigate(`/jobs/edit/${jobId}`)
  }

  const handleDeleteJob = async (job: Job) => {
    const label = job.title || job.job_number || 'this job'
    if (
      !window.confirm(
        `Are you sure you want to delete "${label}"? This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      const { error } = await supabase.from('jobs').delete().eq('id', job.id)
      if (error) throw error
      setJobs((prev) => prev.filter((j) => j.id !== job.id))
    } catch (error: any) {
      console.error('Error deleting job:', error)
      const msg = error?.message || ''
      if (msg.includes('foreign key') || msg.includes('referenced')) {
        alert(
          'Cannot delete this job because it is linked to other records (time entries, invoices, etc.). Remove those links first.'
        )
      } else {
        alert('Failed to delete job: ' + (msg || 'Unknown error'))
      }
    }
  }

  const JOBS_COLUMNS = [
    { id: 'job_details', label: 'Job Details' },
    { id: 'customer', label: 'Customer' },
    { id: 'property', label: 'Property' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'status', label: 'Status' },
    { id: 'priority', label: 'Priority' },
    { id: 'source', label: 'Source' },
    { id: 'billing', label: 'Billing' },
    { id: 'amount', label: 'Amount' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(JOBS_COLUMNS, { storageKey: 'jobs' })

  const getJobSource = (job: Job): 'direct' | 'proposal' | 'contract' => {
    if (job.contract_id) return 'contract'
    if (job.proposal_id) return 'proposal'
    return 'direct'
  }

  const customerName = (job: Job) =>
    job.customer?.company_name ||
    `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim() ||
    '—'

  const jobPropertyName = (job: Job) => job.property?.name || job.property?.address || ''

  const customerOptions = uniqueFilterOptions(
    jobs.map((job) => (customerName(job) !== '—' ? customerName(job) : null))
  )
  const propertyOptions = uniqueFilterOptions(jobs.map(jobPropertyName))

  const filteredJobs = sortItems(
    jobs.filter((job) => {
      const matchesSearch =
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.job_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.property?.name.toLowerCase().includes(searchTerm.toLowerCase())

      const isUnscheduled = job.status === 'draft' && !job.scheduled_date
      const matchesStatus =
        statusFilter.length === 0 ||
        statusFilter.some((s) => (s === 'unscheduled' ? isUnscheduled : job.status === s))
      const matchesRecurring =
        recurringFilter === 'all' ||
        (recurringFilter === 'recurring' ? !!job.is_recurring : !job.is_recurring)

      return (
        matchesSearch &&
        matchesStatus &&
        matchesRecurring &&
        matchesMultiSelect(job.priority, priorityFilter) &&
        matchesMultiSelect(getJobSource(job), sourceFilter) &&
        matchesMultiSelect(customerName(job), customerFilter) &&
        matchesMultiSelect(jobPropertyName(job), propertyFilter) &&
        matchesMultiSelect(job.billing_type || 'direct', billingFilter) &&
        matchesDateRange(job.scheduled_date, scheduledRange) &&
        matchesDateRange(job.created_at, createdRange)
      )
    }),
    (job) => {
      switch (sortBy) {
        case 'number':
          return job.job_number || ''
        case 'title':
          return job.title || ''
        case 'customer':
          return job.customer?.company_name || ''
        case 'status':
          return job.status || ''
        case 'priority':
          return job.priority || ''
        default:
          return job.scheduled_date || ''
      }
    },
    sortDir
  )

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      case 'scheduled':
        return 'bg-blue-100 text-blue-800'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      case 'pending_review':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Get source badge info
  const getBillingLabel = (billingType?: string) => {
    switch (billingType) {
      case 'contract':
        return 'Contract'
      case 'time_materials':
        return 'T&M'
      case 'included':
        return 'Included'
      default:
        return 'Direct'
    }
  }

  const getSourceBadge = (job: Job) => {
    const source = getJobSource(job)
    switch (source) {
      case 'contract':
        return {
          label: 'Contract',
          color: 'bg-purple-100 text-purple-800',
          tooltip: job.contract?.title || `Contract ${job.contract?.contract_number || ''}`,
        }
      case 'proposal':
        return {
          label: 'Proposal',
          color: 'bg-blue-100 text-blue-800',
          tooltip: job.proposal?.title || `Proposal ${job.proposal?.proposal_number || ''}`,
        }
      case 'direct':
        return {
          label: 'Direct',
          color: 'bg-gray-100 text-gray-800',
          tooltip: 'Created directly',
        }
    }
  }

  const formatTime = (time: string) => {
    if (!time) return 'N/A'
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const formatDate = (date: string) => {
    if (!date) return tc('na')
    return formatFieldDate(date.includes('T') ? date : `${date}T12:00:00`, locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const unscheduledCount = jobs.filter((job) => job.status === 'draft' && !job.scheduled_date).length
  const scheduledCount = jobs.filter((job) => job.status === 'scheduled').length
  const inProgressCount = jobs.filter((job) => job.status === 'in_progress').length
  const completedCount = jobs.filter((job) => job.status === 'completed').length
  const pendingReviewCount = jobs.filter((job) => job.status === 'pending_review').length

  const statusChips = [
    { key: 'all', label: t('tabs.all'), count: jobs.length, active: statusFilter.length === 0 },
    {
      key: 'unscheduled',
      label: t('tabs.unscheduled'),
      count: unscheduledCount,
      active: statusFilter.includes('unscheduled'),
      activeClassName: 'bg-amber-600 text-white border-amber-600',
    },
    {
      key: 'scheduled',
      label: t('tabs.scheduled'),
      count: scheduledCount,
      active: statusFilter.includes('scheduled'),
    },
    {
      key: 'in_progress',
      label: t('tabs.inProgress'),
      count: inProgressCount,
      active: statusFilter.includes('in_progress'),
      activeClassName: 'bg-yellow-500 text-white border-yellow-500',
    },
    {
      key: 'completed',
      label: t('tabs.completed'),
      count: completedCount,
      active: statusFilter.includes('completed'),
      activeClassName: 'bg-green-600 text-white border-green-600',
    },
    {
      key: 'pending_review',
      label: t('tabs.pendingReview'),
      count: pendingReviewCount,
      active: statusFilter.includes('pending_review'),
      activeClassName: 'bg-violet-600 text-white border-violet-600',
    },
  ]

  const selectStatus = (key: string) => {
    setStatusFilter(key === 'all' ? [] : [key])
  }

  const emptyState = (
    <MobileEmptyState
      icon={<CalendarIcon className="w-12 h-12" />}
      title={t('empty.title')}
      description={
        searchTerm || statusFilter.length > 0 || priorityFilter.length > 0
          ? t('empty.adjustFilters')
          : canCreateJob
            ? t('empty.createHint')
            : t('empty.noneAssigned')
      }
    />
  )

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title={t('title')}
        subtitle={<span className="hidden sm:inline">{t('subtitle')}</span>}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/recurring-jobs')}
              className={mobile.secondaryBtn}
            >
              <RefreshCwIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{t('actions.recurring')}</span>
            </button>
            {canCreateJob ? (
              <button
                type="button"
                onClick={() => navigate('/jobs/create')}
                className={mobile.primaryBtn}
              >
                <PlusIcon className="w-4 h-4" />
                <span className="sm:hidden">{t('actions.new')}</span>
                <span className="hidden sm:inline">{t('actions.createJob')}</span>
              </button>
            ) : null}
          </div>
        }
      />

      {/* Mobile: status chips */}
      <div className="lg:hidden">
        <MobileStatusChips
          chips={statusChips}
          onSelect={selectStatus}
        />
        {/* Keep Total Jobs label for parity / tests; chip row is primary UI */}
        <p className="sr-only">{t('tabs.totalJobs')} {jobs.length}</p>
      </div>

      {/* Desktop: stats cards */}
      <div className="hidden lg:grid grid-cols-6 gap-6">
        <button
          type="button"
          onClick={() => selectStatus('unscheduled')}
          className="text-left bg-white p-6 rounded-lg shadow-sm border border-amber-200 hover:border-amber-400"
        >
          <div className="flex items-center">
            <div className="p-2 bg-amber-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-amber-700" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('tabs.unscheduled')}</p>
              <p className="text-2xl font-bold text-gray-900">{unscheduledCount}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => selectStatus('scheduled')}
          className="text-left bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-blue-300"
        >
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('tabs.scheduled')}</p>
              <p className="text-2xl font-bold text-gray-900">{scheduledCount}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => selectStatus('in_progress')}
          className="text-left bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-yellow-300"
        >
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <PlayIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('tabs.inProgress')}</p>
              <p className="text-2xl font-bold text-gray-900">{inProgressCount}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => selectStatus('completed')}
          className="text-left bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-green-300"
        >
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('tabs.completed')}</p>
              <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => selectStatus('pending_review')}
          className="text-left bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-violet-300"
        >
          <div className="flex items-center">
            <div className="p-2 bg-violet-100 rounded-lg">
              <AlertCircleIcon className="w-6 h-6 text-violet-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('tabs.pendingReview')}</p>
              <p className="text-2xl font-bold text-gray-900">{pendingReviewCount}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => selectStatus('all')}
          className="text-left bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-gray-400"
        >
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('tabs.totalJobs')}</p>
              <p className="text-2xl font-bold text-gray-900">{jobs.length}</p>
            </div>
          </div>
        </button>
      </div>

      <ListFilterSortBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t('searchPlaceholder')}
        className="lg:p-6"
        filters={[
          {
            type: 'multi',
            label: t('filters.status'),
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'unscheduled', label: translateJobStatus('unscheduled') },
              { value: 'draft', label: translateJobStatus('draft') },
              { value: 'scheduled', label: translateJobStatus('scheduled') },
              { value: 'in_progress', label: translateJobStatus('in_progress') },
              { value: 'completed', label: translateJobStatus('completed') },
              { value: 'cancelled', label: translateJobStatus('cancelled') },
              { value: 'pending_review', label: translateJobStatus('pending_review') },
            ],
          },
          {
            type: 'multi',
            label: t('filters.priority'),
            value: priorityFilter,
            onChange: setPriorityFilter,
            options: [
              { value: 'high', label: translatePriority('high') },
              { value: 'medium', label: translatePriority('medium') },
              { value: 'low', label: translatePriority('low') },
            ],
          },
          {
            type: 'multi',
            label: t('filters.source'),
            value: sourceFilter,
            onChange: setSourceFilter,
            options: [
              { value: 'direct', label: t('filters.direct') },
              { value: 'proposal', label: t('filters.fromProposal') },
              { value: 'contract', label: t('filters.fromContract') },
            ],
          },
          {
            type: 'multi',
            label: t('filters.customer'),
            value: customerFilter,
            onChange: setCustomerFilter,
            options: customerOptions,
          },
          {
            type: 'multi',
            label: t('filters.property'),
            value: propertyFilter,
            onChange: setPropertyFilter,
            options: propertyOptions,
          },
          {
            type: 'multi',
            label: t('filters.billing'),
            value: billingFilter,
            onChange: setBillingFilter,
            options: [
              { value: 'direct', label: t('filters.direct') },
              { value: 'contract', label: t('filters.contract') },
              { value: 'time_materials', label: t('filters.timeMaterials') },
              { value: 'included', label: t('filters.included') },
            ],
          },
          {
            label: t('filters.recurrence'),
            value: recurringFilter,
            onChange: setRecurringFilter,
            ariaLabel: t('filters.recurrence'),
            options: [
              { value: 'all', label: t('filters.allJobs') },
              { value: 'recurring', label: t('filters.recurringOnly') },
              { value: 'single', label: t('filters.oneTimeOnly') },
            ],
          },
          {
            type: 'daterange',
            label: t('filters.scheduled'),
            value: scheduledRange,
            onChange: setScheduledRange,
          },
          {
            type: 'daterange',
            label: t('filters.created'),
            value: createdRange,
            onChange: setCreatedRange,
          },
        ]}
        sortValue={sortBy}
        onSortChange={setSortBy}
        sortOptions={[
          { value: 'date', label: t('sort.scheduleDate') },
          { value: 'number', label: t('sort.jobNumber') },
          { value: 'title', label: t('sort.title') },
          { value: 'customer', label: t('sort.customer') },
          { value: 'status', label: t('sort.status') },
          { value: 'priority', label: t('sort.priority') },
        ]}
        sortDir={sortDir}
        onSortDirChange={setSortDir}
        trailing={
          <div className="hidden lg:block">
            <ColumnSelector
              columns={JOBS_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </div>
        }
      />

      {/* Mobile card list */}
      <div className="native-mobile-list space-y-2">
        {filteredJobs.length === 0
          ? emptyState
          : filteredJobs.map((job) => {
              const isUnscheduled = job.status === 'draft' && !job.scheduled_date
              return (
                <MobileListRow
                  key={job.id}
                  onClick={() => handleViewJob(job.id)}
                  title={job.title}
                  subtitle={`#${job.job_number}`}
                  badges={
                    <>
                      <span
                        className={`${mobile.badge} ${
                          isUnscheduled
                            ? 'bg-amber-100 text-amber-800'
                            : getStatusColor(job.status)
                        }`}
                      >
                        {isUnscheduled
                          ? translateJobStatus('unscheduled')
                          : translateJobStatus(job.status)}
                      </span>
                      <span className={`${mobile.badge} ${getPriorityColor(job.priority)}`}>
                        {translatePriority(job.priority)}
                      </span>
                    </>
                  }
                  meta={
                    <>
                      <div className="truncate">{customerName(job)}</div>
                      {(job.property?.name || job.property?.address) && (
                        <div className="flex items-center gap-1 truncate">
                          <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">
                            {job.property?.name || job.property?.address}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span>
                          {formatDate(job.scheduled_date || '')}
                          {job.scheduled_start_time
                            ? ` · ${formatTime(job.scheduled_start_time)}`
                            : ''}
                        </span>
                      </div>
                    </>
                  }
                  footer={
                    canEditJob || job.is_recurring ? (
                      <>
                        {canEditJob && isUnscheduled && (
                          <MobileRowAction tone="warning" onClick={() => editJob(job.id)}>
                            {t('actions.schedule')}
                          </MobileRowAction>
                        )}
                        {canEditJob && (
                          <MobileRowAction onClick={() => editJob(job.id)}>
                            <EditIcon className="w-4 h-4" />
                            {t('actions.edit')}
                          </MobileRowAction>
                        )}
                        {job.is_recurring && (
                          <MobileRowAction tone="success" onClick={() => openUpcomingJobsModal(job)}>
                            <CalendarIcon className="w-4 h-4" />
                            {t('actions.upcoming')}
                          </MobileRowAction>
                        )}
                        {canEditJob && (
                          <MobileRowAction
                            tone="danger"
                            className="flex-none min-w-[44px] px-3"
                            onClick={() => handleDeleteJob(job)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </MobileRowAction>
                        )}
                      </>
                    ) : undefined
                  }
                />
              )
            })}
      </div>

      {/* Desktop table */}
      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="w-full">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {visibleColumns.has('job_details') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Details
                  </th>
                )}
                {visibleColumns.has('customer') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                )}
                {visibleColumns.has('property') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                )}
                {visibleColumns.has('schedule') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                )}
                {visibleColumns.has('status') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                )}
                {visibleColumns.has('priority') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                )}
                {visibleColumns.has('source') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                )}
                {canSeePricing && visibleColumns.has('billing') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billing
                  </th>
                )}
                {canSeePricing && visibleColumns.has('amount') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                )}
                {visibleColumns.has('actions') && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50">
                  {visibleColumns.has('job_details') && (
                    <td className="px-3 py-3 min-w-0">
                      <div>
                        <button
                          onClick={() => handleViewJob(job.id)}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                        >
                          {job.title}
                        </button>
                        <div className="text-sm text-gray-500 truncate">#{job.job_number}</div>
                        {job.contract && (
                          <div className="text-xs text-gray-400">
                            From Contract: {job.contract.contract_number}
                          </div>
                        )}
                        {job.proposal && !job.contract && (
                          <div className="text-xs text-gray-400">
                            From Proposal: {job.proposal.proposal_number}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {visibleColumns.has('customer') && (
                    <td className="px-3 py-3 min-w-0">
                      <div className="text-sm text-gray-900 truncate">{customerName(job)}</div>
                    </td>
                  )}
                  {visibleColumns.has('property') && (
                    <td className="px-3 py-3 min-w-0">
                      <div>
                        <div className="text-sm text-gray-900 truncate">{job.property?.name}</div>
                        <div className="text-sm text-gray-500 truncate">{job.property?.address}</div>
                      </div>
                    </td>
                  )}
                  {visibleColumns.has('schedule') && (
                    <td className="px-3 py-3 min-w-0">
                      <div>
                        <div className="text-sm text-gray-900 truncate">
                          {formatDate(job.scheduled_date || '')}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {formatTime(job.scheduled_start_time || '')} -{' '}
                          {formatTime(job.scheduled_end_time || '')}
                        </div>
                      </div>
                    </td>
                  )}
                  {visibleColumns.has('status') && (
                    <td className="px-3 py-3 min-w-0">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          job.status === 'draft' && !job.scheduled_date
                            ? 'bg-amber-100 text-amber-800'
                            : getStatusColor(job.status)
                        }`}
                      >
                        {job.status === 'draft' && !job.scheduled_date
                          ? translateJobStatus('unscheduled')
                          : translateJobStatus(job.status)}
                      </span>
                    </td>
                  )}
                  {visibleColumns.has('priority') && (
                    <td className="px-3 py-3 min-w-0">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                          job.priority
                        )}`}
                      >
                        {translatePriority(job.priority)}
                      </span>
                    </td>
                  )}
                  {visibleColumns.has('source') && (
                    <td className="px-3 py-3 min-w-0">
                      {(() => {
                        const badge = getSourceBadge(job)
                        return (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}
                            title={badge.tooltip}
                          >
                            {badge.label}
                          </span>
                        )
                      })()}
                    </td>
                  )}
                  {canSeePricing && visibleColumns.has('billing') && (
                    <td className="px-3 py-3 min-w-0 text-sm text-gray-700">
                      {getBillingLabel(job.billing_type)}
                    </td>
                  )}
                  {canSeePricing && visibleColumns.has('amount') && (
                    <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                      {job.quoted_amount ? `$${job.quoted_amount.toLocaleString()}` : 'N/A'}
                    </td>
                  )}
                  {visibleColumns.has('actions') && (
                    <td className="px-3 py-3 min-w-0 text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {canEditJob && job.status === 'draft' && !job.scheduled_date && (
                          <button
                            onClick={() => editJob(job.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-md"
                            title="Schedule and assign this job"
                          >
                            <CalendarIcon className="w-3.5 h-3.5" />
                            Schedule
                          </button>
                        )}
                        {canEditJob && (
                          <button
                            onClick={() => editJob(job.id)}
                            className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                            title="Edit Job"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                        )}
                        {job.is_recurring && (
                          <button
                            onClick={() => openUpcomingJobsModal(job)}
                            className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-md transition-colors"
                            title="View Upcoming Jobs"
                          >
                            <CalendarIcon className="w-4 h-4" />
                          </button>
                        )}
                        {canEditJob && (
                          <button
                            onClick={() => handleDeleteJob(job)}
                            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete Job"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                        {!canEditJob && !job.is_recurring && (
                          <button
                            onClick={() => handleViewJob(job.id)}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredJobs.length === 0 && emptyState}
      </div>

      <UpcomingJobsModal
        isOpen={upcomingJobsModal.isOpen}
        onClose={closeUpcomingJobsModal}
        jobId={upcomingJobsModal.jobId}
        jobData={upcomingJobsModal.jobData}
      />
    </MobilePage>
  )
}
